import isEmpty from 'lodash/isEmpty'
import isString from 'lodash/isString'
import map from 'lodash/map'
import some from 'lodash/some'
import { useContext, useEffect } from 'react'

import { useTranslation } from '@Globals/Translations'
import { FormContext } from '@Shared/Components/Forms/FormContext'
import { FormState, FormValidation, FormValidationAction, FormValidationFunction } from '@Shared/Components/Forms/FormElement.types'

const runFormValidation: <T extends unknown>(translator, value: T, validations?: FormValidationFunction<unknown>[]) => FormValidation
    = (translator, value, validations) => {
      if (!validations) {
        return { validity: FormState.VALID, validationResponses: [] }
      }
      const validationResults = map(validations, validation => validation(value, translator))
      const inValid = some(validationResults, validation => validation && validation.state === FormState.ERROR)
      const validity = !inValid ? FormState.VALID : FormState.ERROR
      return { validity, validationResponses: validationResults }
    }

export const useFormContext = (
  formKey: string,
  formValidations?: FormValidationFunction<unknown>[],
) => {
  const { t } = useTranslation()
  const {
    blurElement,
    model,
    setActiveID,
    submitAttempts,
    updateModel,
    updateValidationFunctions,
    updateValidationResults,
    validateOn,
    validationResults,
  } = useContext(FormContext)
  const runValidation = (value) => {
    const formValidation = runFormValidation(t, value, formValidations)
    updateValidationResults(formKey, formValidation)
    return formValidation.validity === FormState.VALID
  }

  useEffect(() => {
    updateValidationFunctions(formKey, () => runValidation(model[formKey]))
  })
  const runValidationFromKey = () => {
    if (model) {
      runValidation(
        isString(model[formKey]) ? model[formKey].trimEnd() : model[formKey],
      )
    }
  }
  const validateOnBlur = () => {
    if (validateOn === FormValidationAction.BLUR) {
      runValidationFromKey()
      blurElement()
    }
  }
  const validateOnAlways = (value, bypass?) => {
    if (bypass) {
      runValidation(value)
    }
    if (!validateOn || validateOn === FormValidationAction.ALWAYS) {
      runValidation(value)
    }
  }

  useEffect(() => {
    runValidationFromKey()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitAttempts])

  useEffect(() => {
    if (model) {
      const value = model[formKey]
      if (!isEmpty(value)) {
        runValidation(value)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const updateValue = (value, bypass?) => {
    validateOnAlways(value, bypass)
    setActiveID(formKey)
    updateModel({ ...model, [formKey]: value })
  }

  return {
    model,
    submitAttempts,
    updateValue,
    validateOnBlur,
    runValidation,
    validation: validationResults[formKey],
    value: model[formKey],
  }
}
